import AuthService from "../../base/services/authentication.service";
import { httpService } from "../../base/services/httpService.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { usePersonalDataStore } from "../../pages/auth/authStore";
import { BillingReason, SubscriptionStatus } from "./billingCodes";
import { getStoredBillingStatus, isBillingOnly, isHeldByGate, useBillingStore } from "./billingStore";
import { announceBillingRequired, announceOrganizationLock } from "./paymentRequired";

// What the sign-in responses say about billing.
//
// With the new billing flow on, /Account/login, /Account/confirmEmail and
// /Account/selectOrganization embed a billing status for every organization
// they list (`organizations[].billingStatus`), and log in every account whose
// password is right and whose email is confirmed — an ended trial, a failed
// payment and an unfinished sign up included. With it off, the status is null
// and those accounts are refused at the login form, as they always were.
//
// So the status being there at all is how the app knows which flow the backend
// is running: there is no switch on this side to keep in step with the server's.

/**
 * Each listed organization's billing status, by organization id. Empty when the
 * backend reported none — the new flow is off.
 *
 * The login and confirmEmail responses list organizations under `user`; the
 * selectOrganization response is read under either spelling, and a single
 * `billingStatus` on it is taken to be the selected organization's.
 */
export const readOrganizationStatuses = (data, selectedOrganizationId) => {
   const organizations = data?.user?.organizations ?? data?.organizations ?? [];
   const statuses = {};

   organizations.forEach((organization) => {
      if (organization?.organizationId && organization.billingStatus) {
         statuses[organization.organizationId] = organization.billingStatus;
      }
   });

   if (selectedOrganizationId && data?.billingStatus) {
      statuses[selectedOrganizationId] = data.billingStatus;
   }

   return statuses;
};

/** True when a list of organizations carries billing statuses: the new flow is on. */
export const hasBillingStatuses = (organizations) =>
   Array.isArray(organizations) && organizations.some((organization) => organization?.billingStatus);

/**
 * Whether a selectOrganization response lets the user manage the organization's
 * billing (`canManageBilling`). A response without the field counts as no.
 */
export const readCanManageBilling = (data) => data?.canManageBilling === true;

// Only somebody who may manage an organization's billing is sent to its
// billing page while it is held at billing_only. Anybody else — a member — is
// kept out of the organization: turned away at the login form when it is
// their only one, shown it locked in the organization list when they have
// others, and taken out of it when it is held while they are in it (Routes.js).

// The roles the backend lets manage an organization's billing
// (BillingAuthorizationService), and the permission that lets any other role
// do it. The sign in lists each organization's roles but not
// `canManageBilling`, which only selectOrganization answers, so an
// organization nobody has asked selectOrganization about yet is judged by them.
const BILLING_ROLES = ["Organization", "OrgSuperAdmin"];
const BILLING_PERMISSION = "ManagePayment";

/** True when the roles listed for an organization let the user manage its billing. */
export const holdsBillingRole = (organization) =>
   (organization?.roles ?? []).some(
      (role) =>
         BILLING_ROLES.includes(role?.name) ||
         (role?.permissions ?? []).some((permission) => permission?.name === BILLING_PERMISSION)
   );

/**
 * The status of the organization a selectOrganization response is for, when
 * it keeps this user out: held at billing_only, and not theirs to pay for.
 * Null when they may open it.
 */
export const readOrganizationLock = (data, organizationId) => {
   const status = readOrganizationStatuses(data, organizationId)[organizationId];
   return isBillingOnly(status) && !readCanManageBilling(data) ? status : null;
};

/**
 * True when an organization the user belongs to (an entry of the sign in's or
 * getUserOrganizations' list) is held at billing_only — by its status, or by a
 * 402 its status hasn't caught up with — and they can't manage its billing.
 * What selectOrganization said about it decides, when it has been asked;
 * until then its roles do.
 */
export const isOrganizationLocked = (organization) => {
   const organizationId = organization?.organizationId;
   if (!organizationId) return false;
   if (!isBillingOnly(getStoredBillingStatus(organizationId)) && !isHeldByGate(organizationId)) {
      return false;
   }

   const store = useBillingStore.getState();
   const answered =
      store.billingRights[organizationId] ??
      (organizationId === AuthService.getOrganizationId() ? store.canManageBilling : undefined);
   return !(typeof answered === "boolean" ? answered : holdsBillingRole(organization));
};

/**
 * Keep what a selectOrganization response that turned the user away says
 * about that organization — its status, and that its billing isn't theirs —
 * without selecting it: the organization list shows it locked from then on.
 */
export const rememberLockedOrganization = (data, organizationId) => {
   const status = readOrganizationStatuses(data, organizationId)[organizationId];
   const store = useBillingStore.getState();

   if (status) store.setStatus(organizationId, status);
   store.rememberBillingRight(organizationId, readCanManageBilling(data));
};

/**
 * Keep what a sign-in response says, in place of whatever was held before it.
 * Who may manage billing is only known once an organization is selected.
 */
export const rememberSignInStatuses = (data) => {
   const store = useBillingStore.getState();
   store.replaceStatuses(readOrganizationStatuses(data));
   store.setCanManageBilling(false);
};

/**
 * Keep what a selectOrganization response says about the organization picked,
 * next to what the sign-in already said about the others, and whether this
 * user may manage its billing.
 */
export const rememberSelectedStatus = (data, organizationId) => {
   const statuses = readOrganizationStatuses(data, organizationId);
   const store = useBillingStore.getState();

   Object.entries(statuses).forEach(([id, status]) => store.setStatus(id, status));
   store.setCanManageBilling(readCanManageBilling(data));
   store.rememberBillingRight(organizationId, readCanManageBilling(data));
};

// An organization's level — business (Customer Feedback) or organization — is
// what the menu, the business redirect and the plans offered go by, and they
// read it from the stored user as they render (accountLevel.service.js). The
// sign in stores it, from `user.organizations[].organizationLevel`.
//
// POST /Payment/subscriptions changes it: the server sets the level from the
// packet picked, and says nothing of it in its answer. So does POST
// /Payment/change-plan, to a plan of the other kind of account. So after a plan
// is picked or changed the organization is read again, the way the sign in
// reads it.

/**
 * The level a sign-in response reports for one organization, or null when it
 * reports none.
 *
 * Where selectOrganization puts the level is not documented, so it is read
 * from the shapes the statuses are read from: the organization's entry in the
 * list under `user` or on its own, or a single `organizationLevel` taken to be
 * the selected organization's.
 */
export const readOrganizationLevel = (data, organizationId) => {
   const organizations = data?.user?.organizations ?? data?.organizations;
   const listed = Array.isArray(organizations)
      ? organizations.find((organization) => organization?.organizationId === organizationId)
      : undefined;

   return [listed?.organizationLevel, data?.organizationLevel].find(Number.isInteger) ?? null;
};

/**
 * A user with one organization's level replaced and every other organization
 * left as it was. The same user back when there is nothing to change: no level
 * reported, the organization not listed, or already on that level.
 */
export const withOrganizationLevel = (user, organizationId, level) => {
   const organizations = user?.organizations;

   if (
      !Number.isInteger(level) ||
      !Array.isArray(organizations) ||
      !organizations.some(
         (organization) =>
            organization?.organizationId === organizationId &&
            organization.organizationLevel !== level
      )
   ) {
      return user;
   }

   return {
      ...user,
      organizations: organizations.map((organization) =>
         organization?.organizationId === organizationId
            ? { ...organization, organizationLevel: level }
            : organization
      )
   };
};

/**
 * Keep the level the server reports for an organization in the stored user.
 * Nothing is kept when it reports none: the level is the server's to say.
 *
 * The profile's store holds its own copy of the user the login returned, and
 * writes all of it back over the stored user on its next change — a new name,
 * a profile picture — so that copy is corrected too, or it would put the old
 * level back.
 */
export const storeOrganizationLevel = (organizationId, level) => {
   const user = AuthService.getUser();
   const updatedUser = withOrganizationLevel(user, organizationId, level);
   if (updatedUser !== user) AuthService.setUser(updatedUser);

   const { personalData } = usePersonalDataStore.getState();
   const updatedPersonalData = withOrganizationLevel(personalData, organizationId, level);
   if (updatedPersonalData !== personalData) {
      usePersonalDataStore.setState({ personalData: updatedPersonalData });
   }
};

// The rest of what the sign in keeps from selectOrganization: the token, its
// expiry and the user's roles in the organization. Only while that organization
// is still the one selected — a switch to another in the meantime has stored
// that one's — and only what the response carries, since a role list stored as
// undefined breaks every role check that reads it back. The refresh token held
// is never read back to be written again: a sign in that stored an undefined
// one left a value that throws when it is read.
const rememberSelectedSession = (data, organizationId) => {
   if (AuthService.getOrganizationId() !== organizationId) return;

   if (data.token) LocalStorageService.setItem("atoken", data.token);
   if (data.refreshToken) LocalStorageService.setItem("rtoken", data.refreshToken);
   if (data.tokenExpire) LocalStorageService.setItem("tokenExpire", data.tokenExpire);
   if (Array.isArray(data.userRole)) LocalStorageService.setItem("roles", data.userRole);
};

// A re-read still unanswered by then is given up on, rather than holding the
// organization behind a spinner for the minute a request may otherwise take.
const REFRESH_TIMEOUT_MS = 10000;

/**
 * Read the signed-in organization again, the way the sign in does (GET
 * /Account/selectOrganization), and keep the session and the level it reports.
 *
 * Returns the response's data, or null when the read failed. It never throws:
 * whatever prompted it has already gone through, and a failed read only leaves
 * the old level in place until the next sign in.
 */
export async function refreshSelectedOrganization(organizationId) {
   const userId = AuthService.getUser()?.id;
   if (!organizationId || !userId) return null;

   try {
      const params = new URLSearchParams({ userId, organizationId });
      const response = await httpService.get(
         `/Account/selectOrganization?${params.toString()}`,
         undefined,
         undefined,
         { timeout: REFRESH_TIMEOUT_MS }
      );
      const data = response?.data;
      if (!data || typeof data !== "object") return null;

      // The level first: it is what the re-read is for, and nothing about the
      // session should cost it.
      storeOrganizationLevel(organizationId, readOrganizationLevel(data, organizationId));
      rememberSelectedSession(data, organizationId);
      if (AuthService.getOrganizationId() === organizationId) {
         useBillingStore.getState().setCanManageBilling(readCanManageBilling(data));
         useBillingStore.getState().rememberBillingRight(organizationId, readCanManageBilling(data));
      }
      return data;
   } catch {
      return null;
   }
}

// A first read that fails is tried once more after this long. The usual reason
// is an access token that ran out while the app was closed: the response
// interceptor renews it as that first read fails, and the second uses the new one.
const APP_LOAD_RETRY_MS = 2000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Keep what POST /Account/refreshToken says about the organization its token
 * is for (the base/utils/http.js interceptor renews the session through it).
 *
 * The access token lasts minutes, so the refresh is how a signed-in session
 * hears that a grace period has run out, or that a payment has let the
 * organization back in. With the new flow on it answers the same billingStatus,
 * organizationLevel and canManageBilling selectOrganization does, and they are
 * kept where that keeps them: a billing_only status takes the routes to the
 * billing page, a full one back into the app.
 *
 * Only what the response carries replaces anything. With the flow off its
 * billingStatus is null, and the status the sign in left is kept, not dropped:
 * that is the app start's call (refreshSelectedOrganizationOnLoad), not every
 * renewal's. A backend from before the change answers neither the level nor
 * canManageBilling, so a missing one is kept too.
 */
export const rememberRefreshedSession = (data, organizationId) => {
   if (!data || !organizationId || AuthService.getOrganizationId() !== organizationId) return;

   // The level before the status, as after a plan pick: a status that lets the
   // organization in renders the menu by whatever level is stored by then.
   storeOrganizationLevel(organizationId, readOrganizationLevel(data, organizationId));

   const store = useBillingStore.getState();
   if (typeof data.canManageBilling === "boolean") {
      store.setCanManageBilling(data.canManageBilling);
      store.rememberBillingRight(organizationId, data.canManageBilling);
   }

   if (!data.billingStatus) return;

   const stored = getStoredBillingStatus(organizationId);
   const wasHeld = isBillingOnly(stored) || isHeldByGate(organizationId);
   store.setStatus(organizationId, withBillingStatusOnlyFields(data.billingStatus, stored));
   if (!wasHeld && isBillingOnly(data.billingStatus)) {
      announceBillingRequired(data.billingStatus.reason);
   }
};

// What only GET billing-status reads, from Stripe: the refresh's status is the
// backend's local one, with none of it, and an ended annual plan that still
// owes money is SUBSCRIPTION_CANCELED there rather than OUTSTANDING_DEBT.
const BILLING_STATUS_ONLY = ["amountDue", "actionClientSecret", "paymentFailureMessage"];

const isSameState = (a, b) =>
   Boolean(a && b) &&
   a.accessLevel === b.accessLevel &&
   a.subscriptionStatus === b.subscriptionStatus &&
   a.packetId === b.packetId &&
   Boolean(a.requiresAction) === Boolean(b.requiresAction);

/**
 * A refresh's status, keeping what a billing-status read had added while
 * nothing has changed since: the amount owed, the secret of the bank's check,
 * the debt reason. Without it a page left open loses them at the next refresh
 * (every few minutes), and the billing page swaps the debt for the plans. Once
 * the state has moved, the refresh's status is taken as it is.
 */
export const withBillingStatusOnlyFields = (refreshed, stored) => {
   if (!isSameState(refreshed, stored)) return refreshed;

   const merged = { ...refreshed };
   BILLING_STATUS_ONLY.forEach((key) => {
      if (merged[key] == null && stored[key] != null) merged[key] = stored[key];
   });
   if (
      stored.reason === BillingReason.OUTSTANDING_DEBT &&
      refreshed.reason === BillingReason.SUBSCRIPTION_CANCELED
   ) {
      merged.reason = stored.reason;
   }
   return merged;
};

/**
 * Read the selected organization again as the app starts, and keep what
 * selectOrganization says now: the level, who may manage billing, and the
 * billing status, which it replaces (none at all means the new flow is off
 * for it). A session can outlive all three: the flag switched on or off, a
 * plan picked elsewhere, a role changed. Never rejects.
 */
export async function refreshSelectedOrganizationOnLoad(organizationId) {
   let data = await refreshSelectedOrganization(organizationId);
   if (!data) {
      await wait(APP_LOAD_RETRY_MS);
      data = await refreshSelectedOrganization(organizationId);
   }
   if (!data || AuthService.getOrganizationId() !== organizationId) return;

   const status = readOrganizationStatuses(data, organizationId)[organizationId];
   const store = useBillingStore.getState();
   if (status) {
      store.setStatus(organizationId, status);
   } else {
      store.forget(organizationId);
   }
}

/**
 * Select one of the signed-in user's organizations, as picking it from the
 * organization list does, for a link that names it (a billing email's
 * `?organizationId=`). Resolves false, selecting nothing, when the user isn't a
 * member of it or selectOrganization refuses. Never rejects.
 */
export async function switchOrganization(organizationId) {
   const user = AuthService.getUser();
   const organization = user?.organizations?.find(
      (candidate) => candidate?.organizationId === organizationId
   );
   if (!user?.id || !organization) return false;

   try {
      const params = new URLSearchParams({ userId: user.id, organizationId });
      const response = await httpService.get(
         `/Account/selectOrganization?${params.toString()}`,
         undefined,
         undefined,
         { headers: { "X-OrganizationId": organizationId } }
      );
      const data = response?.data;
      if (!data?.token) return false;

      // Held, and not theirs to pay for: nothing is selected, and the
      // organization list it falls back to shows it locked.
      const lock = readOrganizationLock(data, organizationId);
      if (lock) {
         rememberLockedOrganization(data, organizationId);
         announceOrganizationLock(lock.reason, organization.organizationName);
         return false;
      }

      // The session first, then the status: the status is what makes the
      // routes look again, and they read the selected organization as they do.
      LocalStorageService.setItem("atoken", data.token);
      if (data.refreshToken) LocalStorageService.setItem("rtoken", data.refreshToken);
      if (data.tokenExpire) LocalStorageService.setItem("tokenExpire", data.tokenExpire);
      if (Array.isArray(data.userRole)) LocalStorageService.setItem("roles", data.userRole);
      LocalStorageService.setItem("organizationName", organization.organizationName ?? "");
      LocalStorageService.setItem("organizationId", organizationId);
      storeOrganizationLevel(organizationId, readOrganizationLevel(data, organizationId));
      rememberSelectedStatus(data, organizationId);
      return true;
   } catch {
      return false;
   }
}

/**
 * Keep what POST /Payment/subscriptions (or POST /Payment/change-plan) answered
 * for the signed-in organization — once the organization has been read again
 * for the level the plan gave it. The order is the point: a status that says
 * "full" takes the organization into the app at once, and the app renders its
 * menu and its redirects by whatever level is stored by then.
 *
 * The subscription's own status is the one kept, not the re-read's, because it
 * is also the one the billing page goes on to decide by, and the routes must
 * decide the same way. The re-read's stands in only when the subscription
 * answered with none. A re-read that fails lets the organization in all the
 * same, on its old level.
 *
 * Returns the status kept.
 */
export async function rememberNewSubscription(organizationId, status) {
   const refreshed = await refreshSelectedOrganization(organizationId);
   const kept =
      status ?? readOrganizationStatuses(refreshed, organizationId)[organizationId] ?? null;

   useBillingStore.getState().setStatus(organizationId, kept);
   return kept;
}

/**
 * A sign in that belongs back in the sign up flow: an account whose only
 * organization was registered and never picked a plan. The sign up flow is where
 * a plan is picked, with both kinds of account laid out side by side, so that is
 * where it goes — the same place the flow that predates this one sent it.
 *
 * Not one that has used its free trial (`trialAvailable: false`), which is one
 * whose subscription Stripe no longer has: the sign up promises a trial, so the
 * billing page's plans, which don't, are where that one picks a plan.
 *
 * Only for whoever may pick that plan: a member of such an organization is
 * turned away at the login form like a member of any held one.
 */
export const isUnfinishedSignUp = (organizations) => {
   if (!Array.isArray(organizations) || organizations.length !== 1) return false;

   const status = organizations[0]?.billingStatus;
   return (
      holdsBillingRole(organizations[0]) &&
      isBillingOnly(status) &&
      status.reason === BillingReason.NO_SUBSCRIPTION &&
      status.trialAvailable !== false
   );
};

/** How many days before a trial ends the "add a card" reminder is shown. */
export const TRIAL_REMINDER_DAYS = Object.freeze([1, 3, 15]);

/**
 * The days left on a free trial that has no card behind it, on a day the
 * reminder is due — or null when there is nothing to remind anybody of.
 */
export const getTrialReminderDays = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.TRIALING &&
   !status.hasPaymentMethod &&
   TRIAL_REMINDER_DAYS.includes(status.trialDaysLeft)
      ? status.trialDaysLeft
      : null;
